import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { fetchInterviewEvaluations, groupEvaluationsByInterviewSlot } from "../utils/evaluationUtils";
import "./InterviewEvaluationViewer.css";

/**
 * Component to display interview evaluations for a specific candidate and interview slot
 */
const InterviewEvaluationViewer = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const interviewSlotId = searchParams.get("interviewSlotId");
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupedEvaluations, setGroupedEvaluations] = useState({});
  const [selectedSlotId, setSelectedSlotId] = useState(interviewSlotId || null);

  useEffect(() => {
    const loadEvaluations = async () => {
      try {
        if (!email) {
          setError("Candidate email is required");
          setLoading(false);
          return;
        }
        setLoading(true);
        // If interviewSlotId is provided, fetch evaluations for that slot only
        // Otherwise, fetch all evaluations for the candidate
        const fetchedEvaluations = await fetchInterviewEvaluations(
          email,
          interviewSlotId || null
        );
        setEvaluations(fetchedEvaluations);
        // Group evaluations by interview slot
        const grouped = groupEvaluationsByInterviewSlot(fetchedEvaluations);
        setGroupedEvaluations(grouped);
        // If no specific slot was selected and we have slots, select the first one
        if (!selectedSlotId && Object.keys(grouped).length > 0) {
          setSelectedSlotId(Object.keys(grouped)[0]);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error loading evaluations:", err);
        setError("Failed to load interview evaluations");
        setLoading(false);
      }
    };
    loadEvaluations();
  }, [email, interviewSlotId]);

  const handleSlotChange = (event) => {
    setSelectedSlotId(event.target.value);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (Object.keys(groupedEvaluations).length === 0) {
    return <div className="no-data">No evaluations available for this candidate.</div>;
  }

  const selectedEvaluations = groupedEvaluations[selectedSlotId] || [];

  return (
    <div className="interview-evaluation-viewer">
      <div className="header">
        <h2>Interview Evaluations</h2>
        <p className="candidate-email">Candidate: {email}</p>
      </div>

      <div className="slot-selector">
        <label htmlFor="slot-select">Select Interview Slot:</label>
        <select
          id="slot-select"
          value={selectedSlotId || ""}
          onChange={handleSlotChange}
        >
          {Object.keys(groupedEvaluations).map((slotId) => (
            <option key={slotId} value={slotId}>
              Slot {slotId}
            </option>
          ))}
        </select>
      </div>

      <div className="evaluations-list">
        {selectedEvaluations.length === 0 ? (
          <div className="no-data">No evaluations for this slot.</div>
        ) : (
          selectedEvaluations.map((evaluation) => (
            <div key={evaluation.id} className="evaluation-card">
              <h3>Evaluation {evaluation.id}</h3>

              {/* Video Section */}
              {evaluation.videoURL && (
                <div className="video-section">
                  <h4>Interview Video</h4>
                  <video className="interview-video" controls>
                    <source src={evaluation.videoURL} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}

              {/* Transcript Section */}
              {evaluation.transcript && (
                <div className="transcript-section">
                  <h4>Transcript</h4>
                  <div className="transcript-content">
                    <pre>{evaluation.transcript}</pre>
                  </div>
                </div>
              )}

              {/* Evaluation Data Section */}
              {evaluation.evaluation && (
                <div className="evaluation-section">
                  <h4>Evaluation Data</h4>
                  <pre>
                    {typeof evaluation.evaluation === 'object'
                      ? JSON.stringify(evaluation.evaluation, null, 2)
                      : evaluation.evaluation}
                  </pre>
                </div>
              )}

              {/* Feedback Section */}
              {evaluation.feedbackForAI && (
                <div className="feedback-section">
                  <h4>AI Feedback</h4>
                  <p>{evaluation.feedbackForAI}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default InterviewEvaluationViewer;
